import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import { MAX_GUARDIANS } from "../lib/memberValidation";
import { createGuardianFormValue, type AddressFormValue, type GuardianFormValue } from "../lib/memberForm";
import { C, font } from "../theme";
import { AddressFields } from "./AddressFields";
import { Button } from "./Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { FieldInput, FieldTextarea } from "./Field";

interface GuardianEditorProps {
  guardians: GuardianFormValue[];
  onChange: (guardians: GuardianFormValue[]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  focusField?: string | null;
}

const labelStyle: CSSProperties = {
  display: "block",
  fontFamily: font,
  fontSize: 12,
  fontWeight: 600,
  color: C.gray,
  marginBottom: 4,
  marginTop: 12,
};

const errorStyle: CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };

function focusIndex(focusField: string | null | undefined): number | null {
  const match = focusField?.match(/^guardian-(\d+)-/);
  return match ? Number(match[1]) : null;
}

export function GuardianEditor({ guardians, onChange, disabled, errors = {}, focusField }: GuardianEditorProps) {
  const keySeed = useRef(0);
  const [expandedKey, setExpandedKey] = useState<string | null>(() => guardians[0]?.key ?? null);
  const [pendingRemovalKey, setPendingRemovalKey] = useState<string | null>(null);
  const atLimit = guardians.length >= MAX_GUARDIANS;
  const pendingRemovalIndex = guardians.findIndex(guardian => guardian.key === pendingRemovalKey);
  const pendingRemoval = pendingRemovalIndex >= 0 ? guardians[pendingRemovalIndex] : null;

  const forcedIndex = focusIndex(focusField);
  const forcedKey = forcedIndex !== null ? guardians[forcedIndex]?.key : undefined;

  useEffect(() => {
    if (forcedKey) setExpandedKey(forcedKey);
  }, [forcedKey]);

  useEffect(() => {
    if (expandedKey && !guardians.some(guardian => guardian.key === expandedKey)) {
      setExpandedKey(guardians[0]?.key ?? null);
    }
  }, [guardians, expandedKey]);

  const updateGuardian = (index: number, patch: Partial<GuardianFormValue>) => {
    onChange(guardians.map((guardian, current) => (current === index ? { ...guardian, ...patch } : guardian)));
  };

  const updateAddress = (index: number, address: AddressFormValue) => {
    const guardian = guardians[index];
    if (!guardian) return;
    updateGuardian(index, { address });
  };

  const addGuardian = () => {
    if (atLimit) return;
    keySeed.current += 1;
    const guardian = { ...createGuardianFormValue(), key: `new-guardian-${keySeed.current}` };
    setExpandedKey(guardian.key);
    onChange([...guardians, guardian]);
  };

  const toggleGuardian = (key: string) => {
    setExpandedKey(current => (current === key ? null : key));
  };

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    onChange(guardians.filter(guardian => guardian.key !== pendingRemoval.key));
    setPendingRemovalKey(null);
  };

  return (
    <div>
      {guardians.length === 0 && (
        <div style={{ color: C.light, fontSize: 12.5, background: C.bg, borderRadius: 10, padding: 12 }}>
          Nenhum responsável cadastrado. Este bloco é opcional e não impede o cadastro do aluno.
        </div>
      )}

      {guardians.map((guardian, index) => {
        const errorKey = (field: string) => `guardian-${index}-${field}`;
        const expanded = (forcedKey ?? expandedKey) === guardian.key;
        const hasErrors = Object.keys(errors).some(key => key.startsWith(`guardian-${index}-`));
        const name = guardian.name.trim();
        const phone = guardian.phone.trim();
        const summary = name || "Novo responsável — preencha os dados";
        const panelId = `guardian-${index}-panel`;
        const headerId = `guardian-${index}-header`;

        return (
          <div
            key={guardian.key}
            style={{
              border: `1px solid ${hasErrors ? C.red : C.faint}`,
              borderRadius: 12,
              padding: expanded ? 14 : 12,
              marginTop: 12,
              background: expanded ? C.white : C.bg,
              transition: "background-color 180ms ease, border-color 180ms ease, padding 180ms ease",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                id={headerId}
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => toggleGuardian(guardian.key)}
                disabled={disabled}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: disabled ? "default" : "pointer",
                  textAlign: "left",
                  fontFamily: font,
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: C.ink, fontSize: 12.5, fontWeight: 700 }}>Responsável {index + 1}</span>
                    {hasErrors && <span style={{ color: C.red, fontSize: 10.5, fontWeight: 700 }}>Revisar</span>}
                  </span>
                  <span style={{ display: "block", color: name ? C.gray : C.light, fontSize: 11.5, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {summary}{phone ? ` · ${phone}` : ""}
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  style={{ flexShrink: 0, color: C.gray, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}
                />
              </button>
              <Button
                variant="ghost"
                ariaLabel={`Remover responsável ${index + 1}`}
                title={`Remover responsável ${index + 1}`}
                disabled={disabled}
                onClick={() => setPendingRemovalKey(guardian.key)}
                style={{ color: C.red, padding: 4, display: "inline-flex", flexShrink: 0 }}
              >
                <Trash2 size={15} aria-hidden="true" />
              </Button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateRows: expanded ? "1fr" : "0fr",
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? "auto" : "none",
                transition: "grid-template-rows 220ms ease, opacity 160ms ease",
              }}
              aria-hidden={!expanded}
              inert={!expanded}
            >
              <div style={{ overflow: "hidden", minHeight: 0 }}>
                <div id={panelId} role="region" aria-labelledby={headerId}>
                  <label htmlFor={errorKey("name")} style={labelStyle}>Nome *</label>
                  <FieldInput
                    id={errorKey("name")}
                    value={guardian.name}
                    onChange={event => updateGuardian(index, { name: event.target.value })}
                    maxLength={121}
                    disabled={disabled}
                  />
                  {errors[errorKey("name")] && <div style={errorStyle}>{errors[errorKey("name")]}</div>}

                  <label htmlFor={errorKey("phone")} style={labelStyle}>Telefone</label>
                  <FieldInput
                    id={errorKey("phone")}
                    type="tel"
                    value={guardian.phone}
                    onChange={event => updateGuardian(index, { phone: event.target.value })}
                    placeholder="(11) 98765-4321"
                    disabled={disabled}
                  />
                  {errors[errorKey("phone")] && <div style={errorStyle}>{errors[errorKey("phone")]}</div>}

                  <div style={{ marginTop: 4 }}>
                    <div style={{ color: C.gray, fontSize: 12, fontWeight: 600 }}>Endereço</div>
                    <AddressFields
                      idPrefix={`guardian-${index}-address`}
                      value={guardian.address}
                      onChange={address => updateAddress(index, address)}
                      disabled={disabled}
                      errors={{
                        zipCode: errors[errorKey("zip-code")],
                        state: errors[errorKey("state")],
                      }}
                    />
                  </div>

                  <label htmlFor={errorKey("notes")} style={labelStyle}>Observações</label>
                  <FieldTextarea
                    id={errorKey("notes")}
                    value={guardian.notes}
                    onChange={event => updateGuardian(index, { notes: event.target.value })}
                    maxLength={1000}
                    disabled={disabled}
                  />
                  {errors[errorKey("notes")] && <div style={errorStyle}>{errors[errorKey("notes")]}</div>}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <Button variant="outline" onClick={addGuardian} disabled={disabled || atLimit}>
          + Adicionar responsável
        </Button>
        {atLimit && <span style={{ color: C.light, fontSize: 11.5 }}>Limite de {MAX_GUARDIANS} responsáveis atingido.</span>}
      </div>

      {pendingRemoval && (
        <ConfirmDialog
          title="Remover responsável"
          confirmLabel="Remover"
          onConfirm={confirmRemoval}
          onCancel={() => setPendingRemovalKey(null)}
        >
          Tem certeza que deseja remover{" "}
          <b style={{ color: C.ink }}>
            {pendingRemoval.name.trim() || `Responsável ${pendingRemovalIndex + 1}`}
          </b>
          ? Os dados preenchidos neste responsável serão descartados.
        </ConfirmDialog>
      )}
    </div>
  );
}
